import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCampaign } from '../contexts/CampaignContext'
import { useModal } from '../contexts/ModalContext'
import MarkdownText from '../components/MarkdownText'
import DescriptionSections from '../components/DescriptionSections'
import LocationForm from '../components/forms/LocationForm'
import { zoomAnchored } from '../utils/mapZoom'

function InteractiveMapPage() {
  const [locations, setLocations] = useState([])
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [showGrid, setShowGrid] = useState(false)

  // Admin pin-management state
  const [placementCoords, setPlacementCoords] = useState(null)        // { x, y } → show placement modal
  const [editPinLocation, setEditPinLocation] = useState(null)         // location → show edit-pin modal
  const [assignState, setAssignState] = useState(null)                 // { x, y } → show assign-to-existing modal
  const [assignSelection, setAssignSelection] = useState('')           // dropdown value in assign modal
  const [repositioning, setRepositioning] = useState(null)             // location being repositioned
  const [locationFormCoords, setLocationFormCoords] = useState(null)   // { x, y } → inject into new LocationForm
  const [locationFormOpen, setLocationFormOpen] = useState(false)
  const [editingFromPin, setEditingFromPin] = useState(null)           // location being edited via pin modal

  const navigate = useNavigate()
  const { campaign, canEdit, realCanEdit, isModuleEnabled, loading: campaignLoading } = useCampaign()
  const { viewAsPlayer, toggleViewAsPlayer, authEpoch } = useAuth()
  const { alert: showAlert, confirm } = useModal()
  const mapRef = useRef(null)

  // The container node is captured via a callback ref rather than a plain useRef because the
  // guards further down return `null` while `campaignLoading` is true — on that first render
  // there is no container at all, so a mount-only effect reading `containerRef.current` would
  // find nothing and (with `[]` deps) never look again. `containerReady` re-runs the wheel
  // effect at the moment the node actually appears.
  const containerRef = useRef(null)
  const [containerReady, setContainerReady] = useState(false)
  const attachContainer = useCallback((node) => {
    containerRef.current = node
    setContainerReady(!!node)
  }, [])

  const mapUrl = campaign?.mapImageUrl
  const mapWidth = campaign?.mapWidth
  const mapHeight = campaign?.mapHeight

  // Drag state held in a ref to avoid stale-closure issues in document-level handlers
  const dragRef = useRef({
    active: false,
    moved: false,
    startMouseX: 0,
    startMouseY: 0,
    startPosX: 0,
    startPosY: 0,
    target: null, // The IMG element clicked (if any)
  })

  // Mirror certain React state into a ref so document-level mouseup can read current values
  // without re-binding the effect on every change
  const handlerStateRef = useRef({ canEdit, showGrid, repositioning })
  useEffect(() => {
    handlerStateRef.current = { canEdit, showGrid, repositioning }
  }, [canEdit, showGrid, repositioning])

  // Live scale/position for handlers bound outside the render scope (wheel, drag). Written
  // synchronously at every mutation point — a wheel can fire several times inside one frame,
  // and React batches those, so a commit-time-only mirror would hand the second event a stale
  // view and lose the anchor.
  const viewRef = useRef({ scale, position })
  useEffect(() => {
    viewRef.current = { scale, position }
  }, [scale, position])

  const applyView = useCallback((next) => {
    viewRef.current = next
    setScale(next.scale)
    setPosition(next.position)
  }, [])

  // Zoom while holding `anchor` (client coords) still on screen. Defaults to the container's
  // centre, so the +/− buttons keep whatever you're looking at in place instead of letting the
  // growing map shove the view sideways.
  const zoomTo = useCallback((nextScale, anchor) => {
    const rect = containerRef.current?.getBoundingClientRect()
    const center = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : null
    applyView(zoomAnchored({ ...viewRef.current, nextScale, center, anchor }))
  }, [applyView])

  // authEpoch: see the identical comment in components/tabs/ItemsTab.jsx.
  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch('/api/locations')
      const data = await res.json()
      setLocations(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching locations:', err)
    } finally {
      setLoading(false)
    }
  }, [authEpoch])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  // Derive pins from locations that have coordinates set
  const pinnedLocations = locations.filter(
    loc => loc.map_x !== null && loc.map_x !== undefined && loc.map_y !== null && loc.map_y !== undefined
  )

  // Helper function to get label position styles
  const getLabelPositionStyle = (position = 'below') => {
    switch (position) {
      case 'above':
        return { bottom: '40px', left: '50%', transform: 'translateX(-50%)', top: 'auto' }
      case 'left':
        return { right: '40px', top: '50%', transform: 'translateY(-50%)', left: 'auto' }
      case 'right':
        return { left: '40px', top: '50%', transform: 'translateY(-50%)' }
      case 'below':
      default:
        return { top: '40px', left: '50%', transform: 'translateX(-50%)' }
    }
  }

  // ---- API helpers for pin updates ----

  const patchLocation = async (body) => {
    const res = await fetch('/api/locations', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || 'Failed to update location')
    }
  }

  const saveCoordsForExisting = async (locationId, x, y) => {
    try {
      await patchLocation({ id: locationId, map_x: x, map_y: y })
      await fetchLocations()
    } catch (err) {
      showAlert({ title: 'Error', message: err.message, type: 'error' })
    }
  }

  const removePinFromLocation = async (location) => {
    await confirm({
      title: 'Remove Pin',
      message: `Remove the map pin for "${location.name}"? The location itself will NOT be deleted — only its pin position is cleared.`,
      confirmText: 'Remove Pin',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await patchLocation({ id: location.id, map_x: null, map_y: null })
          await fetchLocations()
          showAlert({
            title: 'Pin Removed',
            message: `Pin for "${location.name}" has been removed from the map.`,
            type: 'success',
          })
        } catch (err) {
          showAlert({ title: 'Error', message: err.message, type: 'error' })
        }
      },
    })
  }

  const cycleLabelPosition = async (location) => {
    const order = ['below', 'above', 'left', 'right']
    const current = location.map_label_position || 'below'
    const next = order[(order.indexOf(current) + 1) % order.length]
    try {
      await patchLocation({ id: location.id, map_label_position: next })
      await fetchLocations()
      // Refresh the editPinLocation with the new value so the modal stays in sync
      setEditPinLocation(prev => prev && prev.id === location.id ? { ...prev, map_label_position: next } : prev)
    } catch (err) {
      showAlert({ title: 'Error', message: err.message, type: 'error' })
    }
  }

  // ---- Compute map coords from a mouseup, using the original IMG element stored at mousedown time ----

  const coordsFromRelease = (clientX, clientY, imgEl) => {
    const rect = imgEl.getBoundingClientRect()
    const x = ((clientX - rect.left) / rect.width) * 100
    const y = ((clientY - rect.top) / rect.height) * 100
    return {
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
    }
  }

  // ---- Marker interactions ----

  const handleMarkerClick = (location, e) => {
    e.stopPropagation()
    if (showGrid && canEdit) {
      setEditPinLocation(location)
    } else {
      setSelectedLocation(location)
    }
  }

  const closeModal = () => setSelectedLocation(null)

  // ---- Zoom controls ----

  const handleZoomIn = () => zoomTo(viewRef.current.scale + 0.25)
  const handleZoomOut = () => zoomTo(viewRef.current.scale - 0.25)
  const handleResetView = () => applyView({ scale: 1, position: { x: 0, y: 0 } })

  // ---- Pan + click-to-place (distinguishes click from drag via movement threshold) ----

  const DRAG_THRESHOLD_PX = 4

  const handleMouseDown = (e) => {
    if (e.target !== mapRef.current && e.target.tagName !== 'IMG') return

    dragRef.current = {
      active: true,
      moved: false,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPosX: viewRef.current.position.x,
      startPosY: viewRef.current.position.y,
      target: e.target.tagName === 'IMG' ? e.target : null,
    }
    setIsDragging(true)
  }

  // Document-level handlers, set up once on mount — use dragRef + handlerStateRef so they
  // always see the latest values without re-binding on every state change.
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return
      const dx = e.clientX - dragRef.current.startMouseX
      const dy = e.clientY - dragRef.current.startMouseY
      if (!dragRef.current.moved && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
        dragRef.current.moved = true
      }
      const nextPosition = {
        x: dragRef.current.startPosX + dx,
        y: dragRef.current.startPosY + dy,
      }
      viewRef.current = { ...viewRef.current, position: nextPosition }
      setPosition(nextPosition)
    }

    const onUp = (e) => {
      if (!dragRef.current.active) return
      const { moved, target } = dragRef.current
      const { canEdit: admin, showGrid: grid, repositioning: repos } = handlerStateRef.current

      dragRef.current.active = false
      setIsDragging(false)

      // If mouse didn't move (i.e. a click), and we're in admin pin mode, and the click was on the IMG
      if (!moved && admin && grid && target) {
        const coords = coordsFromRelease(e.clientX, e.clientY, target)

        if (repos) {
          // Completing a reposition flow
          ;(async () => {
            try {
              await patchLocation({ id: repos.id, map_x: coords.x, map_y: coords.y })
              await fetchLocations()
              showAlert({
                title: 'Pin Moved',
                message: `"${repos.name}" is now at x: ${coords.x}%, y: ${coords.y}%.`,
                type: 'success',
              })
              setRepositioning(null)
            } catch (err) {
              showAlert({ title: 'Error', message: err.message, type: 'error' })
            }
          })()
        } else {
          setPlacementCoords(coords)
        }
      }
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Wheel zoom with passive:false so preventDefault works. Re-runs on `containerReady` because
  // the container does not exist on the first render (see the campaignLoading guard below).
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const wheelHandler = (e) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      // Anchor on the cursor so the spot you're pointing at stays put.
      zoomTo(viewRef.current.scale + delta, { x: e.clientX, y: e.clientY })
    }

    container.addEventListener('wheel', wheelHandler, { passive: false })
    return () => container.removeEventListener('wheel', wheelHandler)
  }, [containerReady, zoomTo])

  // ---- Placement modal handlers ----

  const handleCreateNewFromPlacement = () => {
    if (!placementCoords) return
    setLocationFormCoords(placementCoords)
    setLocationFormOpen(true)
    setPlacementCoords(null)
  }

  const handleAssignFromPlacement = () => {
    if (!placementCoords) return
    setAssignState(placementCoords)
    setAssignSelection('')
    setPlacementCoords(null)
  }

  const handleAssignSubmit = async () => {
    if (!assignState || !assignSelection) return
    try {
      await patchLocation({
        id: parseInt(assignSelection, 10),
        map_x: assignState.x,
        map_y: assignState.y,
      })
      await fetchLocations()
      const loc = locations.find(l => l.id === parseInt(assignSelection, 10))
      showAlert({
        title: 'Pin Placed',
        message: `"${loc?.name || 'Location'}" pinned at x: ${assignState.x}%, y: ${assignState.y}%.`,
        type: 'success',
      })
    } catch (err) {
      showAlert({ title: 'Error', message: err.message, type: 'error' })
    } finally {
      setAssignState(null)
      setAssignSelection('')
    }
  }

  // ---- LocationForm submit (creating new location from map click) ----

  const handleLocationFormSubmit = async (formData, iconFile, deleteIconFlag) => {
    try {
      const payload = {
        ...formData,
        map_x: locationFormCoords?.x ?? null,
        map_y: locationFormCoords?.y ?? null,
        map_label_position: 'below',
      }

      const response = await fetch('/api/locations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        showAlert({ title: 'Error', message: err.error || 'Failed to create location', type: 'error' })
        return
      }

      const result = await response.json()
      const newId = result.id

      // Upload icon if one was attached (same pattern as LocationsTab)
      if (iconFile) {
        const uploadFormData = new FormData()
        uploadFormData.append('icon', iconFile)
        uploadFormData.append('location_id', newId)
        const uploadResponse = await fetch('/api/locations/upload-icon', {
          method: 'POST',
          body: uploadFormData,
        })
        if (!uploadResponse.ok) {
          const err = await uploadResponse.json().catch(() => ({}))
          showAlert({
            title: 'Warning',
            message: `Location saved but icon upload failed: ${err.error || 'unknown error'}`,
            type: 'error',
          })
        }
      }

      await fetchLocations()
      showAlert({
        title: 'Location Created',
        message: `"${formData.name}" has been created and pinned on the map.`,
        type: 'success',
      })
    } catch (err) {
      showAlert({ title: 'Error', message: 'Failed to create location', type: 'error' })
    } finally {
      setLocationFormCoords(null)
    }
  }

  // ---- Edit-pin modal actions ----

  const startReposition = () => {
    if (!editPinLocation) return
    setRepositioning(editPinLocation)
    setEditPinLocation(null)
    showAlert({
      title: 'Reposition Mode',
      message: `Click anywhere on the map to set the new position for "${editPinLocation.name}".`,
      type: 'info',
    })
  }

  const editPinDetails = () => {
    if (!editPinLocation) return
    // Reuse LocationForm for editing — existing coordinates are already on the location row.
    setLocationFormCoords(null)
    setEditingFromPin(editPinLocation)
    setEditPinLocation(null)
  }

  const handleEditFormSubmit = async (formData, iconFile, deleteIconFlag) => {
    if (!editingFromPin) return
    try {
      const payload = {
        ...formData,
        id: editingFromPin.id,
        // Preserve existing coordinates when editing from the pin modal
        map_x: editingFromPin.map_x,
        map_y: editingFromPin.map_y,
        map_label_position: editingFromPin.map_label_position || 'below',
      }
      const response = await fetch('/api/locations', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        showAlert({ title: 'Error', message: err.error || 'Failed to update location', type: 'error' })
        return
      }

      // Icon handling (same pattern as LocationsTab)
      const locationId = editingFromPin.id
      if (deleteIconFlag) {
        await fetch('/api/locations/delete-icon', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location_id: locationId }),
        })
      } else if (iconFile) {
        const uploadFormData = new FormData()
        uploadFormData.append('icon', iconFile)
        uploadFormData.append('location_id', locationId)
        await fetch('/api/locations/upload-icon', { method: 'POST', body: uploadFormData })
      }

      await fetchLocations()
      showAlert({
        title: 'Location Updated',
        message: `"${formData.name}" has been updated.`,
        type: 'success',
      })
    } catch (err) {
      showAlert({ title: 'Error', message: 'Failed to update location', type: 'error' })
    } finally {
      setEditingFromPin(null)
    }
  }

  // Candidate locations for the "assign to existing" dropdown:
  // only locations that do NOT already have coordinates.
  const unpinnedLocations = locations
    .filter(l => l.map_x === null || l.map_x === undefined || l.map_y === null || l.map_y === undefined)
    .sort((a, b) => a.name.localeCompare(b.name))

  // ---- Guards: module disabled, or enabled but no map uploaded yet ----
  // Must run before anything below dereferences mapWidth/mapHeight.
  //
  // `campaignLoading` gates the first two checks: CampaignProvider fetches
  // /api/campaigns asynchronously, so `campaign` (and therefore isModuleEnabled)
  // is briefly null/false on every fresh load. Redirecting on that transient
  // state would bounce a legitimate map straight back to the campaign page
  // before the real data ever arrives.

  if (campaignLoading) {
    return null
  }

  if (!isModuleEnabled('map')) {
    return <Navigate to={campaign ? `/c/${campaign.slug}/campaign` : '/campaigns'} replace />
  }

  if (!mapUrl || !mapWidth || !mapHeight) {
    return (
      <div className="map-empty" style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        padding: '2rem',
      }}>
        <h2 style={{ margin: 0 }}>No map yet</h2>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          {canEdit
            ? 'Upload a map in campaign settings to start placing pins.'
            : 'Your DM hasn’t added a map for this campaign yet.'}
        </p>
      </div>
    )
  }

  // ---- Render ----

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: 'var(--primary-dark)',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1000,
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        background: 'var(--primary-blue)',
        borderBottom: '2px solid var(--accent-teal)',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 100
      }}>
        <h1 style={{
          color: 'var(--text-primary)',
          fontSize: '1.5rem',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <span>🗺️</span> {campaign?.name}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {realCanEdit && (
            <button
              onClick={toggleViewAsPlayer}
              title={viewAsPlayer
                ? 'Currently previewing as a player — hidden pins are hidden. Click to return to full admin view.'
                : 'Preview the map as a player would see it (hidden pins disappear)'}
              style={{
                background: viewAsPlayer ? 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)' : 'var(--accent-deep)',
                color: '#ffffff',
                border: `1px solid ${viewAsPlayer ? '#27ae60' : 'var(--accent-teal)'}`,
                padding: '0.75rem 1.25rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span>{viewAsPlayer ? '🛡️' : '👁️'}</span>
              <span>{viewAsPlayer ? 'Admin View' : 'User View'}</span>
            </button>
          )}
          {isModuleEnabled('locations') && (
            <button
              onClick={() => navigate(`/c/${campaign.slug}/campaign?tab=locations`)}
              style={{
                background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
                color: '#ffffff',
                border: '1px solid var(--accent-teal)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(42, 85, 102, 0.3)',
              }}
            >
              ← Back to Locations
            </button>
          )}
        </div>
      </div>

      {/* Zoom Controls */}
      <div style={{
        position: 'fixed',
        bottom: '2rem',
        right: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        zIndex: 100
      }}>
        <button
          onClick={handleZoomIn}
          style={zoomButtonStyle}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent-teal)'}
          title="Zoom In"
        >+</button>
        <button
          onClick={handleZoomOut}
          style={zoomButtonStyle}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent-teal)'}
          title="Zoom Out"
        >−</button>
        <button
          onClick={handleResetView}
          style={{ ...zoomButtonStyle, fontSize: '1.2rem' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent-teal)'}
          title="Reset View"
        >⟲</button>
        <div style={{
          background: 'rgba(13, 21, 32, 0.9)',
          color: 'var(--text-light)',
          padding: '0.5rem',
          borderRadius: '6px',
          textAlign: 'center',
          fontSize: '0.85rem',
          fontWeight: 600,
          border: '1px solid var(--accent-teal)'
        }}>
          {Math.round(scale * 100)}%
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setShowGrid(!showGrid)
              if (showGrid) setRepositioning(null)
            }}
            style={{
              ...zoomButtonStyle,
              background: showGrid ? '#e74c3c' : 'var(--accent-teal)',
              fontSize: '1.2rem',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            title={showGrid ? "Exit Admin Pin Mode" : "Enter Admin Pin Mode"}
          >
            📐
          </button>
        )}
      </div>

      {/* Map Container */}
      <div
        ref={attachContainer}
        style={{
          width: '100%',
          height: 'calc(100vh - 80px)',
          position: 'relative',
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : (showGrid && canEdit ? 'crosshair' : 'grab'),
          background: 'var(--primary-dark)'
        }}
      >
        <div
          ref={mapRef}
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${scale})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            userSelect: 'none'
          }}
        >
          <img
            src={mapUrl}
            alt={`Map of ${campaign?.name ?? 'the campaign'}`}
            draggable={false}
            style={{
              display: 'block',
              width: `${mapWidth}px`,
              height: `${mapHeight}px`,
              userSelect: 'none',
              pointerEvents: 'auto'
            }}
          />

          {/* Coordinate Grid (Admin Only) */}
          {showGrid && canEdit && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${mapWidth}px`,
              height: `${mapHeight}px`,
              pointerEvents: 'none',
              zIndex: 5
            }}>
              {/* Vertical lines */}
              {Array.from({ length: 11 }).map((_, i) => (
                <div
                  key={`v-${i}`}
                  style={{
                    position: 'absolute',
                    left: `${i * 10}%`,
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    background: i % 5 === 0 ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 255, 255, 0.3)',
                    pointerEvents: 'none'
                  }}
                />
              ))}
              {/* Horizontal lines */}
              {Array.from({ length: 11 }).map((_, i) => (
                <div
                  key={`h-${i}`}
                  style={{
                    position: 'absolute',
                    top: `${i * 10}%`,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: i % 5 === 0 ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 255, 255, 0.3)',
                    pointerEvents: 'none'
                  }}
                />
              ))}
              {/* Coordinate labels */}
              {Array.from({ length: 11 }).map((_, i) => (
                <div
                  key={`label-x-${i}`}
                  style={{
                    position: 'absolute',
                    left: `${i * 10}%`,
                    top: '10px',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: '#00ffff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    border: '1px solid #00ffff'
                  }}
                >
                  {i * 10}%
                </div>
              ))}
              {Array.from({ length: 11 }).map((_, i) => (
                <div
                  key={`label-y-${i}`}
                  style={{
                    position: 'absolute',
                    top: `${i * 10}%`,
                    left: '10px',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: '#00ffff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    border: '1px solid #00ffff'
                  }}
                >
                  {i * 10}%
                </div>
              ))}
            </div>
          )}

          {/* Interactive Markers — driven by locations with map_x/map_y set */}
          {!loading && pinnedLocations.map((location) => {
            // Fog of war: only render if visible or admin
            const shouldShow = location.is_visible === 1 || canEdit
            if (!shouldShow) return null

            const isBeingRepositioned = repositioning && repositioning.id === location.id

            return (
              <div
                key={location.id}
                onClick={(e) => handleMarkerClick(location, e)}
                style={{
                  position: 'absolute',
                  left: `${location.map_x}%`,
                  top: `${location.map_y}%`,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  zIndex: 10,
                  pointerEvents: 'auto',
                  opacity: isBeingRepositioned ? 0.3 : 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.3)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'
                }}
                title={location.name}
              >
                {/* Marker Pin — renders uploaded icon_url as a circular image, or a solid dot as fallback */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: location.icon_url
                    ? 'var(--primary-dark)'
                    : (location.is_visible === 1
                        ? 'radial-gradient(circle, var(--accent-teal) 0%, var(--accent-cyan) 100%)'
                        : 'radial-gradient(circle, #6b7f91 0%, #4a5a6a 100%)'),
                  border: `4px solid ${location.is_visible === 1 ? 'var(--accent-teal)' : '#6b7f91'}`,
                  borderRadius: '50%',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                  filter: location.icon_url && location.is_visible === 0 ? 'grayscale(0.7) brightness(0.7)' : 'none',
                }}>
                  {location.icon_url ? (
                    <img
                      src={location.icon_url}
                      alt={location.name}
                      draggable={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '14px',
                      height: '14px',
                      background: 'var(--text-light)',
                      borderRadius: '50%'
                    }} />
                  )}
                </div>

                {/* Label */}
                <div style={{
                  position: 'absolute',
                  ...getLabelPositionStyle(location.map_label_position),
                  background: 'rgba(13, 21, 32, 0.95)',
                  color: 'var(--text-light)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  border: '2px solid var(--accent-teal)',
                  pointerEvents: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.7)'
                }}>
                  {location.name}
                  {location.is_visible === 0 && canEdit && (
                    <span style={{
                      marginLeft: '0.5rem',
                      color: 'var(--text-muted)',
                      fontSize: '0.9rem'
                    }}>
                      (Hidden)
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Instructions */}
      <div style={{
        position: 'fixed',
        bottom: '2rem',
        left: '2rem',
        background: 'rgba(13, 21, 32, 0.9)',
        color: 'var(--text-light)',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid var(--accent-teal)',
        fontSize: '0.9rem',
        maxWidth: '280px',
        zIndex: 100
      }}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Map Controls:</div>
        <div>🖱️ Click & drag to pan</div>
        <div>🔍 Mouse wheel to zoom</div>
        <div>📍 Click markers for info</div>
        {canEdit && showGrid && !repositioning && (
          <>
            <div style={{ color: '#00ffff', marginTop: '0.5rem', fontWeight: 600 }}>
              📐 Admin Pin Mode
            </div>
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              • Click empty map → place new pin<br />
              • Click existing pin → edit/move/remove
            </div>
          </>
        )}
        {canEdit && showGrid && repositioning && (
          <>
            <div style={{ color: '#ffa500', marginTop: '0.5rem', fontWeight: 600 }}>
              ⤴ Repositioning "{repositioning.name}"
            </div>
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              Click anywhere on the map to set its new position.
            </div>
            <button
              onClick={() => setRepositioning(null)}
              style={{
                marginTop: '0.5rem',
                background: 'transparent',
                color: 'var(--text-light)',
                border: '1px solid var(--accent-teal)',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              Cancel Reposition
            </button>
          </>
        )}
      </div>

      {/* Placement Modal (Admin) */}
      {placementCoords && (
        <AdminDialog onClose={() => setPlacementCoords(null)} title="Place New Pin">
          <div style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>
            Coordinates: <strong style={{ color: 'var(--accent-cyan)' }}>
              x: {placementCoords.x}%, y: {placementCoords.y}%
            </strong>
          </div>
          <div style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Attach this pin to an existing location, or create a new one at this spot.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button style={primaryButtonStyle} onClick={handleCreateNewFromPlacement}>
              + Create New Location Here
            </button>
            <button
              style={{ ...primaryButtonStyle, background: 'var(--accent-deep)' }}
              onClick={handleAssignFromPlacement}
              disabled={unpinnedLocations.length === 0}
              title={unpinnedLocations.length === 0 ? 'All locations already have pins' : ''}
            >
              📍 Assign to Existing Location ({unpinnedLocations.length})
            </button>
            <button style={secondaryButtonStyle} onClick={() => setPlacementCoords(null)}>
              Cancel
            </button>
          </div>
        </AdminDialog>
      )}

      {/* Assign-to-Existing Modal (Admin) */}
      {assignState && (
        <AdminDialog
          onClose={() => { setAssignState(null); setAssignSelection('') }}
          title="Assign Pin to Existing Location"
        >
          <div style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>
            Coordinates: <strong style={{ color: 'var(--accent-cyan)' }}>
              x: {assignState.x}%, y: {assignState.y}%
            </strong>
          </div>
          <label style={{
            display: 'block',
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
            fontSize: '0.95rem',
            fontWeight: 500,
          }}>
            Select Location
          </label>
          <select
            value={assignSelection}
            onChange={(e) => setAssignSelection(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'var(--accent-deep)',
              border: '1px solid var(--accent-teal)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '0.95rem',
              marginBottom: '1.5rem',
              cursor: 'pointer',
            }}
          >
            <option value="">— Choose a location —</option>
            {unpinnedLocations.map(loc => (
              <option key={loc.id} value={loc.id}>
                {loc.name} ({loc.location_type}){loc.is_visible === 0 ? ' [hidden]' : ''}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              style={{ ...primaryButtonStyle, flex: 1 }}
              onClick={handleAssignSubmit}
              disabled={!assignSelection}
            >
              Place Pin
            </button>
            <button
              style={{ ...secondaryButtonStyle, flex: 1 }}
              onClick={() => { setAssignState(null); setAssignSelection('') }}
            >
              Cancel
            </button>
          </div>
        </AdminDialog>
      )}

      {/* Edit-Pin Modal (Admin) */}
      {editPinLocation && (
        <AdminDialog onClose={() => setEditPinLocation(null)} title={`Edit Pin: ${editPinLocation.name}`}>
          <div style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
            Current position: x: {editPinLocation.map_x}%, y: {editPinLocation.map_y}%
          </div>
          <div style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Label position: <strong>{editPinLocation.map_label_position || 'below'}</strong>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button style={primaryButtonStyle} onClick={editPinDetails}>
              ✏️ Edit Location Details
            </button>
            <button style={primaryButtonStyle} onClick={startReposition}>
              ⤴ Reposition Pin
            </button>
            <button
              style={{ ...primaryButtonStyle, background: 'var(--accent-deep)' }}
              onClick={() => cycleLabelPosition(editPinLocation)}
            >
              🔄 Cycle Label Position (below → above → left → right)
            </button>
            <button
              style={{ ...primaryButtonStyle, background: '#7a2a2a' }}
              onClick={() => {
                const loc = editPinLocation
                setEditPinLocation(null)
                removePinFromLocation(loc)
              }}
            >
              🗑️ Remove Pin
            </button>
            <button style={secondaryButtonStyle} onClick={() => setEditPinLocation(null)}>
              Cancel
            </button>
          </div>
        </AdminDialog>
      )}

      {/* LocationForm — used for both create (from placement) and edit (from pin modal) */}
      <LocationForm
        isOpen={locationFormOpen}
        onClose={() => { setLocationFormOpen(false); setLocationFormCoords(null) }}
        onSubmit={handleLocationFormSubmit}
      />
      <LocationForm
        isOpen={!!editingFromPin}
        location={editingFromPin}
        onClose={() => setEditingFromPin(null)}
        onSubmit={handleEditFormSubmit}
      />

      {/* Location Detail Modal (player-facing marker click) */}
      {selectedLocation && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(5, 10, 18, 0.9)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
              border: '2px solid var(--accent-teal)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '1.5rem',
              gap: '1rem',
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flex: 1 }}>
                {selectedLocation.icon_url && (
                  <img
                    src={selectedLocation.icon_url}
                    alt={selectedLocation.name}
                    style={{
                      width: '96px',
                      height: '96px',
                      borderRadius: '12px',
                      objectFit: 'cover',
                      border: '2px solid var(--accent-teal)',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div>
                  <h2 style={{
                    color: 'var(--text-primary)',
                    marginBottom: '0.5rem',
                    fontSize: '2rem',
                    fontWeight: 500
                  }}>
                    {selectedLocation.name}
                  </h2>
                  {selectedLocation.location_type && (
                    <span style={{
                      background: 'var(--accent-teal)',
                      color: 'var(--text-light)',
                      padding: '0.3rem 0.8rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }}>
                      {selectedLocation.location_type}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--accent-teal)',
                  color: 'var(--text-light)',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.8,
              fontSize: '1rem'
            }}>
              <MarkdownText style={{ whiteSpace: 'pre-line' }}>
                {selectedLocation.full_description || selectedLocation.description || 'No description available.'}
              </MarkdownText>
              <DescriptionSections sections={selectedLocation.description_sections} />
            </div>

            {selectedLocation.is_visible === 0 && canEdit && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'rgba(107, 127, 145, 0.2)',
                border: '1px solid #6b7f91',
                borderRadius: '8px',
                color: 'var(--text-muted)',
                fontSize: '0.9rem'
              }}>
                ℹ️ This location is hidden from players
              </div>
            )}
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.2);
                opacity: 0.7;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}

// ---- Shared styles ----

const zoomButtonStyle = {
  background: 'var(--accent-teal)',
  color: 'var(--text-primary)',
  border: '2px solid var(--accent-cyan)',
  borderRadius: '8px',
  width: '50px',
  height: '50px',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
  transition: 'all 0.2s ease',
}

const primaryButtonStyle = {
  background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
  color: '#ffffff',
  border: '1px solid var(--accent-teal)',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '0.95rem',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  textAlign: 'center',
}

const secondaryButtonStyle = {
  background: 'transparent',
  color: 'var(--text-light)',
  border: '1px solid var(--accent-teal)',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  fontWeight: 500,
  fontSize: '0.95rem',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  textAlign: 'center',
}

// ---- Shared admin dialog wrapper ----

function AdminDialog({ onClose, title, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(5, 10, 18, 0.9)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2500,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
          border: '2px solid var(--accent-teal)',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        <h2 style={{
          color: 'var(--text-primary)',
          marginTop: 0,
          marginBottom: '1rem',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  )
}

export default InteractiveMapPage
